/**
 * FeedUrlTab Component
 * Tab za unos URL-a feeda i autentikacije (XML ili SOAP)
 */

import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  InputAdornment,
  CircularProgress,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import {
  CloudDownload as FetchIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  Api as SoapIcon,
} from '@mui/icons-material';
import { ParsedFeed } from '../../../lib/xmlParser';
import { SoapConfig } from '../types';

interface FeedUrlTabProps {
  feedName: string;
  onFeedNameChange: (name: string) => void;
  feedUrl: string;
  onFeedUrlChange: (url: string) => void;
  feedType: 'xml' | 'soap' | 'excel';
  onFeedTypeChange: (type: 'xml' | 'soap' | 'excel') => void;
  authType: 'none' | 'url_params' | 'basic';
  onAuthTypeChange: (type: 'none' | 'url_params' | 'basic') => void;
  authUsername: string;
  onAuthUsernameChange: (username: string) => void;
  authPassword: string;
  onAuthPasswordChange: (password: string) => void;
  soapConfig: SoapConfig;
  onSoapConfigChange: (config: SoapConfig) => void;
  extraHeaders: Record<string, string>;
  onExtraHeadersChange: (headers: Record<string, string>) => void;
  deliveryDays: number;
  onDeliveryDaysChange: (days: number) => void;
  loading: boolean;
  onFetchFeed: () => void;
  onLoadFile: (file: File) => void;
  parsedFeed: ParsedFeed | null;
  loadedFileName?: string;
}

const DEFAULT_SOAP_CONFIG: SoapConfig = {
  endpoint_url: '',
  soap_action: '',
  method: '',
  namespace: '',
  username: '',
  password: '',
};

export function FeedUrlTab({
  feedName,
  onFeedNameChange,
  feedUrl,
  onFeedUrlChange,
  feedType,
  onFeedTypeChange,
  authType,
  onAuthTypeChange,
  authUsername,
  onAuthUsernameChange,
  authPassword,
  onAuthPasswordChange,
  soapConfig = DEFAULT_SOAP_CONFIG,
  onSoapConfigChange,
  extraHeaders = {},
  onExtraHeadersChange,
  deliveryDays,
  onDeliveryDaysChange,
  loading,
  onFetchFeed,
  onLoadFile,
  parsedFeed,
  loadedFileName,
}: FeedUrlTabProps) {
  const handleSoapChange = (field: keyof SoapConfig, value: string) => {
    onSoapConfigChange({
      ...soapConfig,
      [field]: value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onLoadFile(file);
    e.target.value = '';
  };

  const isValid = feedType === 'soap'
    ? !!(soapConfig?.endpoint_url && soapConfig?.soap_action && soapConfig?.method && soapConfig?.namespace)
    : !!feedUrl;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Naziv profila"
          value={feedName}
          onChange={(e) => onFeedNameChange(e.target.value)}
          placeholder="npr. ASBIS, AVTERA, CT4Partners..."
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <FormControl fullWidth>
          <InputLabel>Tip izvora</InputLabel>
          <Select
            value={feedType}
            onChange={(e) => onFeedTypeChange(e.target.value as 'xml' | 'soap' | 'excel')}
            label="Tip izvora"
          >
            <MenuItem value="xml">XML / JSON URL</MenuItem>
            <MenuItem value="excel">Excel (XLS/XLSX)</MenuItem>
            <MenuItem value="soap">SOAP Web Service</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          type="number"
          label="Rok isporuke (dana)"
          value={deliveryDays}
          onChange={(e) => onDeliveryDaysChange(parseInt(e.target.value) || 0)}
          inputProps={{ min: 0 }}
          helperText={deliveryDays > 0 ? 'Udaljeni dobavljač' : 'Trenutna zaliha'}
        />
      </Grid>

      {feedType !== 'soap' ? (
        // XML/Excel Feed Configuration
        <>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={feedType === 'excel' ? 'Excel URL (.xls/.xlsx)' : 'Feed URL'}
              value={feedUrl}
              onChange={(e) => onFeedUrlChange(e.target.value)}
              placeholder={feedType === 'excel' ? 'https://example.com/feed.xlsx' : 'https://example.com/feed.xml'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <input
              accept=".xml,.xls,.xlsx"
              hidden
              id="feed-file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="feed-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                disabled={loading}
              >
                Učitaj fajl (XML/XLS/XLSX)
              </Button>
            </label>
            {loadedFileName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Učitan fajl: <code>{loadedFileName}</code>
              </Typography>
            )}
          </Grid>

          {feedType === 'xml' && (
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Autentikacija</InputLabel>
                <Select
                  value={authType}
                  onChange={(e) => onAuthTypeChange(e.target.value as any)}
                  label="Autentikacija"
                >
                  <MenuItem value="none">Bez autentikacije</MenuItem>
                  <MenuItem value="url_params">URL parametri (USERNAME/PASSWORD)</MenuItem>
                  <MenuItem value="basic">Basic Auth</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {feedType === 'xml' && authType !== 'none' && (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Username"
                  value={authUsername}
                  onChange={(e) => onAuthUsernameChange(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  value={authPassword}
                  onChange={(e) => onAuthPasswordChange(e.target.value)}
                />
              </Grid>
            </>
          )}

          {feedType === 'xml' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Extra HTTP headeri (JSON)"
                value={Object.keys(extraHeaders).length > 0 ? JSON.stringify(extraHeaders) : ''}
                onChange={(e) => {
                  const val = e.target.value.trim();
                  if (!val) { onExtraHeadersChange({}); return; }
                  try { onExtraHeadersChange(JSON.parse(val)); } catch { /* ignore */ }
                }}
                placeholder='{"KorisnikX": "KorisnikServisa"}'
                helperText="Opcionalno - dodatni headeri za API poziv (JSON format)"
                size="small"
              />
            </Grid>
          )}
        </>
      ) : (
        // SOAP Configuration
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SoapIcon /> SOAP Konfiguracija
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Endpoint URL"
                  value={soapConfig?.endpoint_url || ''}
                  onChange={(e) => handleSoapChange('endpoint_url', e.target.value)}
                  placeholder="http://example.com/webservices/service.asmx"
                  helperText="URL SOAP servisa (bez ?WSDL)"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SOAP Action"
                  value={soapConfig?.soap_action || ''}
                  onChange={(e) => handleSoapChange('soap_action', e.target.value)}
                  placeholder="http://example.com/B2B/GetProducts"
                  helperText="SOAPAction header vrijednost"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Method name"
                  value={soapConfig?.method || ''}
                  onChange={(e) => handleSoapChange('method', e.target.value)}
                  placeholder="GetProducts_WithAttributes"
                  helperText="Naziv SOAP metode"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Namespace"
                  value={soapConfig?.namespace || ''}
                  onChange={(e) => handleSoapChange('namespace', e.target.value)}
                  placeholder="http://example.com/B2B"
                  helperText="XML namespace za SOAP body"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Kredencijali (ako su potrebni)
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={soapConfig?.username || ''}
                  onChange={(e) => handleSoapChange('username', e.target.value)}
                  placeholder="Korisničko ime za SOAP"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  value={soapConfig?.password || ''}
                  onChange={(e) => handleSoapChange('password', e.target.value)}
                  placeholder="Lozinka za SOAP"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      <Grid item xs={12}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} /> : <FetchIcon />}
          onClick={onFetchFeed}
          disabled={loading || !isValid}
        >
          {loading ? 'Učitavam...' : feedType === 'soap' ? 'Pozovi SOAP i parsiraj' : 'Učitaj i parsiraj feed'}
        </Button>
      </Grid>

      {parsedFeed && (
        <Grid item xs={12}>
          <Alert severity="success">
            Feed učitan! Pronađeno <strong>{parsedFeed.totalRecords}</strong> zapisa.
            Root element: <code>{parsedFeed.rootElement}</code>,
            Record element: <code>{parsedFeed.recordElement}</code>
          </Alert>
        </Grid>
      )}
    </Grid>
  );
}
